import { supabase, supabaseConfigured } from './supabase'

let apiUrl = import.meta.env.VITE_API_URL

if (!apiUrl && typeof window !== 'undefined') {
  const host = window.location.hostname
  if (host === 'dev.nujan.pages.dev' || host.includes('staging') || (host.endsWith('nujan.pages.dev') && host !== 'nujan.pages.dev')) {
    apiUrl = 'https://nujan-api-staging-xhn5f2om3a-uc.a.run.app'
  } else {
    // Production (nujan.app, www.nujan.app, nujan.pages.dev, nujan.com)
    apiUrl = 'https://nujan-api-385782460362.us-central1.run.app'
  }
}

export const API_BASE = apiUrl || 'http://localhost:8000'

// Fast in-memory access token cache to avoid serializing parallel API requests
// behind `supabase.auth.getSession()`'s storage lock on every call.
let _cachedToken: string | undefined
let _cachedTokenValidUntil = 0
let _inflightSessionPromise: Promise<string | undefined> | null = null

if (supabaseConfigured && typeof window !== 'undefined') {
  try {
    supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.access_token) {
        _cachedToken = session.access_token
        _cachedTokenValidUntil = Date.now() + 30_000
      } else {
        _cachedToken = undefined
        _cachedTokenValidUntil = 0
      }
    })
  } catch {
    // Ignore in test environments
  }
}

export async function getFastAccessToken(): Promise<string | undefined> {
  if (!supabaseConfigured) return undefined
  const now = Date.now()
  if (_cachedToken && now < _cachedTokenValidUntil) {
    return _cachedToken
  }
  if (_inflightSessionPromise) {
    return _inflightSessionPromise
  }
  _inflightSessionPromise = (async () => {
    try {
      const { data } = await supabase.auth.getSession()
      const tok = data?.session?.access_token
      _cachedToken = tok
      _cachedTokenValidUntil = tok ? Date.now() + 20_000 : 0
      return tok
    } catch (e) {
      console.warn('[api] Failed to fetch active auth session:', e)
      return _cachedToken
    } finally {
      _inflightSessionPromise = null
    }
  })()
  return _inflightSessionPromise
}

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message)
    this.name = 'ApiError'
  }
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  tokenOverride?: string
): Promise<T> {
  const headers: Record<string, string> = {
    ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
    ...(options.headers as Record<string, string>),
  }

  // Get active session token automatically if Supabase is configured
  const token = tokenOverride || (await getFastAccessToken())

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers })

  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }))
    throw new ApiError(res.status, body.detail || `HTTP ${res.status}`)
  }

  // 204 No Content
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export const api = {
  get:    <T>(path: string, token?: string)                          => request<T>(path, { method: 'GET' }, token),
  post:   <T>(path: string, body: unknown, token?: string)           => request<T>(path, { method: 'POST',   body: JSON.stringify(body) }, token),
  patch:  <T>(path: string, body: unknown, token?: string)           => request<T>(path, { method: 'PATCH',  body: JSON.stringify(body) }, token),
  put:    <T>(path: string, body: unknown, token?: string)           => request<T>(path, { method: 'PUT',    body: JSON.stringify(body) }, token),
  delete: <T>(path: string, token?: string)                          => request<T>(path, { method: 'DELETE' }, token),

  /** Fetch raw binary Blob (e.g. for streaming PDFs) */
  getBlob: async (path: string, token?: string): Promise<Blob> => {
    const authToken = token || (await getFastAccessToken())
    const headers: Record<string, string> = {}
    if (authToken) headers['Authorization'] = `Bearer ${authToken}`
    const res = await fetch(`${API_BASE}${path}`, { method: 'GET', headers })
    if (!res.ok) {
      const errBody = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }))
      throw new ApiError(res.status, errBody.detail || `Failed to stream file: HTTP ${res.status}`)
    }
    const contentType = res.headers.get('content-type') || ''
    if (contentType.includes('application/json') || contentType.includes('text/html')) {
      throw new ApiError(415, `Expected PDF stream but received ${contentType}`)
    }
    const blob = await res.blob()
    if (blob.size === 0) {
      throw new ApiError(404, 'Received empty file binary from server')
    }
    return blob
  },

  /** Upload a file (multipart/form-data) */
  upload: <T>(path: string, formData: FormData, token?: string) => {
    const headers: Record<string, string> = {}
    if (token) headers['Authorization'] = `Bearer ${token}`
    return request<T>(path, { method: 'POST', body: formData, headers }, token)
  },
}

export { ApiError }
